/**
 * The billing-profile form's rules (backend docs/BILLING.md §5, phase 1), kept out of
 * the component so they can be tested.
 *
 * These are LIGHT checks: required fields and the obvious shapes (GSTIN 15 characters,
 * PIN 6 digits). The server is the authority — it checks the GSTIN's checksum, that it
 * belongs to the address's state, the LUT, SAC, IFSC and the rest — and the form shows
 * its `errors` under each field.
 */
import { BillingProfileDTO, GstRegistration } from '../../types/billing';

export const INDIA = 'IN';

/** Who may SAVE a profile; the backend's PUT list, in both role spellings. */
const PROFILE_EDIT_ROLES = [
  'platform_admin',
  'master_admin',
  'super_admin',
  'service_admin',
  'provider_owner',
  'provider_admin',
];

/**
 * Whether to offer the form as editable. An organisation never edits its own profile
 * (whoever bills it does), so viewing one's own is read-only. The server still decides
 * whether the target is BELOW the caller; a 403 there is shown on the form.
 */
export const canEditBillingProfile = (
  role: string | null | undefined,
  targetOrganizationId: string,
  ownOrganizationId?: string | null,
): boolean => {
  const r = (role || '').toLowerCase();
  if (!PROFILE_EDIT_ROLES.includes(r)) return false;
  // The platform edits any profile, its own (the issuer's) included.
  if (r === 'platform_admin' || r === 'master_admin') return true;
  return !ownOrganizationId || ownOrganizationId !== targetOrganizationId;
};

export const isIndia = (p: Pick<BillingProfileDTO, 'countryCode'>): boolean =>
  (p.countryCode || INDIA).toUpperCase() === INDIA;

/** The registrations to offer: OVERSEAS is set by the server for a foreign country, never chosen in India. */
export const registrationOptions = (all: GstRegistration[]): GstRegistration[] =>
  all.filter((r) => r !== 'OVERSEAS');

/** GSTIN applies to an Indian organisation that is registered. */
export const showsGstin = (p: BillingProfileDTO): boolean =>
  isIndia(p) && p.gstRegistration !== 'UNREGISTERED';

/** A registered Indian business's PAN is characters 3–12 of its GSTIN. */
export const panFromGstin = (gstin: string | null | undefined): string | null => {
  const g = (gstin || '').trim().toUpperCase();
  return g.length === 15 ? g.slice(2, 12) : null;
};

/**
 * The profile after the country changes. Abroad: no GST fields, the server's OVERSEAS,
 * free-text state and USD. Back to India: Regular registration and INR. Fields the
 * new country does not use are cleared rather than sent and refused.
 */
export const withCountry = (p: BillingProfileDTO, countryCode: string): BillingProfileDTO => {
  const code = countryCode.toUpperCase();
  const wasIndia = isIndia(p);
  if (code === INDIA) {
    return {
      ...p,
      countryCode: code,
      gstRegistration: !p.gstRegistration || p.gstRegistration === 'OVERSEAS' ? 'REGULAR' : p.gstRegistration,
      taxId: null,
      ...(wasIndia ? {} : { state: null, stateCode: null, invoiceCurrency: 'INR' }),
    };
  }
  return {
    ...p,
    countryCode: code,
    gstRegistration: 'OVERSEAS',
    gstin: null,
    pan: null,
    stateCode: null,
    einvoiceEnabled: false,
    ...(wasIndia ? { state: null, invoiceCurrency: 'USD' } : {}),
  };
};

const blank = (v: string | null | undefined) => !v || !v.trim();

/** Required fields and obvious shapes only; field -> reason, empty when it may be sent. */
export const clientErrors = (p: BillingProfileDTO): Record<string, string> => {
  const e: Record<string, string> = {};
  if (blank(p.legalName)) e.legalName = 'Legal name is required';
  if (blank(p.addressLine1)) e.addressLine1 = 'Address is required';
  if (blank(p.city)) e.city = 'City is required';
  if (blank(p.countryCode)) e.countryCode = 'Choose the country';

  if (isIndia(p)) {
    if (blank(p.postalCode)) e.postalCode = 'PIN code is required';
    else if (!/^\d{6}$/.test(p.postalCode!.trim())) e.postalCode = 'PIN code must be 6 digits';
    if (blank(p.stateCode)) e.stateCode = 'Choose the state';
    if (!p.gstRegistration) e.gstRegistration = 'Choose the GST registration';
    if (showsGstin(p)) {
      if (blank(p.gstin)) e.gstin = 'GSTIN is required for a registered business';
      else if (p.gstin!.trim().length !== 15) e.gstin = 'GSTIN is 15 characters';
    }
  } else if (blank(p.postalCode)) {
    e.postalCode = 'Postal code is required';
  }
  return e;
};

const READ_ONLY: (keyof BillingProfileDTO)[] = [
  'organizationName', 'configured', 'issuedByOrganizationName', 'updatedBy', 'updatedAt',
];

const UPPER: (keyof BillingProfileDTO)[] = ['gstin', 'pan', 'bankIfsc', 'bankSwift', 'lutArn', 'invoicePrefix'];

/**
 * The PUT body: trimmed, blanks as null (a blank "" is a value to the server), codes
 * upper-cased, read-only fields dropped, and fields the country/registration does not
 * use cleared. A registered business's PAN is left to the server, which takes it from
 * the GSTIN — sending a stale one after editing the GSTIN would be refused as a mismatch.
 */
export const toSaveBody = (p: BillingProfileDTO): BillingProfileDTO => {
  const out: any = {};
  Object.entries(p).forEach(([k, v]) => {
    if (READ_ONLY.includes(k as keyof BillingProfileDTO)) return;
    if (typeof v === 'string') {
      const t = v.trim();
      out[k] = t === '' ? null : UPPER.includes(k as keyof BillingProfileDTO) ? t.toUpperCase() : t;
    } else {
      out[k] = v;
    }
  });
  if (out.countryCode) out.countryCode = String(out.countryCode).toUpperCase();
  if (out.billingEmails) {
    out.billingEmails = String(out.billingEmails).split(',').map((m: string) => m.trim()).filter(Boolean).join(',') || null;
  }
  if (isIndia(out)) {
    out.taxId = null;
    if (out.gstRegistration === 'UNREGISTERED') out.gstin = null;
    else out.pan = null;
  } else {
    out.gstRegistration = 'OVERSEAS';
    out.gstin = null;
    out.pan = null;
    out.stateCode = null;
  }
  out.einvoiceEnabled = !!out.einvoiceEnabled;
  return out as BillingProfileDTO;
};

/** Whether any issuer-only field is set, so the collapsed "As an issuer" section opens itself. */
export const hasIssuerFields = (p: BillingProfileDTO): boolean =>
  ['invoicePrefix', 'lutArn', 'lutValidFrom', 'lutValidTo', 'defaultSac', 'bankName', 'bankAccountName',
    'bankAccountNumber', 'bankIfsc', 'bankSwift'].some((k) => !blank((p as any)[k])) || !!p.einvoiceEnabled;

/** Server field errors that belong to the issuer section, so it is opened to show them. */
export const ISSUER_FIELDS = [
  'invoicePrefix', 'lutArn', 'lutValidFrom', 'lutValidTo', 'defaultSac', 'bankName', 'bankAccountName',
  'bankAccountNumber', 'bankIfsc', 'bankSwift', 'einvoiceEnabled',
];
